// @ts-ignore -- Vite/bundler raw fixture import is the browser-safe canonical shape source.
import canonicalShapesTtl from "../fixtures/ux-application-library-shapes-v1.ttl?raw";
// @ts-ignore -- n3 does not publish declarations compatible with this workspace.
import { Parser as N3Parser } from "n3";
// @ts-ignore -- rdf-ext does not publish declarations compatible with this workspace.
import rdf from "rdf-ext";
// @ts-ignore -- rdf-validate-shacl does not publish declarations compatible with this workspace.
import SHACLValidator from "rdf-validate-shacl";
import type { Quad, Term } from "@rdfjs/types";

export type UxApplicationLibraryAdmissionIssueCode =
  | "rdf-input-invalid"
  | "authority-context-invalid"
  | "ontology-root-missing"
  | "shacl-conformance-failed"
  | "library-source-forbidden"
  | "library-presentation-forbidden"
  | "library-event-invalid"
  | "library-capability-invalid"
  | "duplicate-ux-instance-id"
  | "duplicate-library-port-id"
  | "unbound-pattern-port"
  | "incompatible-pattern-port"
  | "ux-pattern-state-invalid"
  | "ux-pattern-transition-invalid"
  | "journey-entry-invalid"
  | "journey-terminal-missing"
  | "journey-step-unreachable"
  | "journey-recovery-invalid"
  | "exact-namespace-collision"
  | "exact-namespace-invalid"
  | "application-envelope-invalid"
  | "duplicate-ux-pattern-ref"
  | "definition-reference-invalid"
  | "application-capability-invalid";

export interface UxApplicationLibraryAdmissionIssue {
  code: UxApplicationLibraryAdmissionIssueCode;
  path: string;
  message: string;
  focusNode?: string;
}

export interface UxApplicationLibraryAdmissionResult {
  ok: boolean;
  issues: UxApplicationLibraryAdmissionIssue[];
}

export const UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS = Object.freeze({
  maxQuads: 4_096,
  maxTermLexicalBytes: 1_048_576,
  maxQuotedTermDepth: 32,
  maxTermNodes: 16_384,
});

const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
const LIBRARY_NS = "https://cogo.xyz/ns/library/v1#";
const XSD = "http://www.w3.org/2001/XMLSchema#";
const p = (local: string) => `${LIBRARY_NS}${local}`;
const idPattern = /^[a-z][a-z0-9-]*$/;
const authorityContextKeys = [
  "admittedUxResearchRefs",
  "approvedCanonicalSourceRefs",
  "admittedApplicationCoreRefs",
] as const;

interface AuthoritySnapshot {
  admittedUxResearchRefs: string[];
  approvedCanonicalSourceRefs: string[];
  admittedApplicationCoreRefs: string[];
}

const classes = {
  application: p("ApplicationBlueprint"),
  definition: p("UXPatternDefinition"),
  sourceAuthority: p("SourceAuthority"),
  applicationCoreAuthority: p("ApplicationCoreAuthority"),
  instance: p("UXPatternInstance"),
  state: p("UxState"),
  transition: p("UxTransition"),
  port: p("LibraryPort"),
  semanticNode: p("SemanticNode"),
  applicationFlow: p("ApplicationFlow"),
  datasource: p("DatasourceRequirement"),
  binding: p("PortBinding"),
  journey: p("JourneyGraph"),
  journeyStep: p("JourneyStep"),
  journeyTransition: p("JourneyTransition"),
} as const;

function parseDataset(ttl: string) {
  const dataset = rdf.dataset();
  for (const quad of new N3Parser().parse(ttl)) dataset.add(quad);
  return dataset;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function safeThrownValue(value: unknown): string {
  if (value === null) return "null";
  switch (typeof value) {
    case "string":
      return value;
    case "number":
    case "boolean":
    case "bigint":
    case "undefined":
      return `${value}`;
    case "symbol":
      return "symbol";
    case "function":
    case "object":
      try {
        const descriptor = Object.getOwnPropertyDescriptor(value, "message");
        if (descriptor && "value" in descriptor) {
          const message = descriptor.value;
          if (typeof message === "string") return message;
        }
      } catch {
        // A hostile Proxy is still rendered without touching its traps again.
      }
      return "non-Error thrown value";
  }
  return "unknown thrown value";
}

function snapshotAuthorityContext(
  candidate: unknown,
):
  | { ok: true; value: AuthoritySnapshot }
  | { ok: false; message: string } {
  try {
    if (!isRecord(candidate)) {
      return {
        ok: false,
        message: "Authority context must be supplied independently.",
      };
    }
    const descriptors = Object.getOwnPropertyDescriptors(candidate);
    if (
      Object.values(descriptors).some(
        (descriptor) => descriptor.get || descriptor.set,
      )
    ) {
      return {
        ok: false,
        message: "Authority context accessors are not stable input.",
      };
    }
    const keys = Object.keys(candidate);
    if (
      keys.length !== authorityContextKeys.length ||
      keys.some(
        (key) =>
          !authorityContextKeys.includes(
            key as (typeof authorityContextKeys)[number],
          ),
      )
    ) {
      return {
        ok: false,
        message: "Authority context must contain only exact admitted ref lists.",
      };
    }
    const cloned = structuredClone(candidate) as Record<string, unknown>;
    const patterns: Record<(typeof authorityContextKeys)[number], RegExp> = {
      admittedUxResearchRefs:
        /^cogo:ux-observation\/[a-z0-9][a-z0-9-]*$/,
      approvedCanonicalSourceRefs:
        /^cogo:approved-source\/[a-z0-9][a-z0-9-]*$/,
      admittedApplicationCoreRefs:
        /^cogo:application-core\/[a-z0-9][a-z0-9-]*$/,
    };
    const snapshot = {} as AuthoritySnapshot;
    for (const key of authorityContextKeys) {
      const refs = cloned[key];
      if (
        !Array.isArray(refs) ||
        !refs.every(
          (ref) => typeof ref === "string" && patterns[key].test(ref),
        )
      ) {
        return {
          ok: false,
          message: "Authority context must contain exact admitted ref lists.",
        };
      }
      snapshot[key] = [...new Set(refs)].sort();
    }
    return { ok: true, value: snapshot };
  } catch (error) {
    return {
      ok: false,
      message: `Authority context could not be snapshotted: ${safeThrownValue(error)}`,
    };
  }
}

type RdfTermSnapshotFailure =
  | "invalid"
  | "cycle"
  | "shared-quoted-quad"
  | "quoted-depth"
  | "term-nodes";

interface RdfTermSnapshot {
  term: Term;
  quotedHeight: number;
}

interface RdfTermSnapshotState {
  readonly counted: WeakSet<object>;
  readonly memo: WeakMap<object, RdfTermSnapshot>;
  termNodes: number;
}

interface RdfTermSnapshotSlot {
  value?: RdfTermSnapshot;
}

type RdfTermSnapshotFrame =
  | {
      kind: "enter";
      candidate: unknown;
      allowedTermTypes: readonly string[];
      quotedDepth: number;
      slot: RdfTermSnapshotSlot;
    }
  | {
      kind: "literal";
      candidate: Record<string, unknown>;
      value: string;
      language: string;
      datatype: RdfTermSnapshotSlot;
      slot: RdfTermSnapshotSlot;
    }
  | {
      kind: "quad";
      candidate: Record<string, unknown>;
      subject: RdfTermSnapshotSlot;
      predicate: RdfTermSnapshotSlot;
      object: RdfTermSnapshotSlot;
      graph: RdfTermSnapshotSlot;
      slot: RdfTermSnapshotSlot;
    };

type RdfTermSnapshotResult =
  | { ok: true; value: RdfTermSnapshot }
  | { ok: false; reason: RdfTermSnapshotFailure };

function snapshotRdfTerm(
  candidate: unknown,
  allowedTermTypes: readonly string[],
  state: RdfTermSnapshotState,
): RdfTermSnapshotResult {
  const active = new WeakSet<object>();
  const nestedQuotedQuads = new WeakSet<object>();
  const root: RdfTermSnapshotSlot = {};
  const stack: RdfTermSnapshotFrame[] = [{
    kind: "enter",
    candidate,
    allowedTermTypes,
    quotedDepth: 0,
    slot: root,
  }];

  while (stack.length > 0) {
    const frame = stack.pop()!;
    if (frame.kind === "literal") {
      const datatype = frame.datatype.value?.term;
      if (!datatype || datatype.termType !== "NamedNode") {
        return { ok: false, reason: "invalid" };
      }
      const term = Object.freeze(
        frame.language
          ? rdf.literal(frame.value, frame.language)
          : rdf.literal(frame.value, datatype),
      ) as Term;
      const snapshot = { term, quotedHeight: 0 };
      active.delete(frame.candidate);
      state.memo.set(frame.candidate, snapshot);
      frame.slot.value = snapshot;
      continue;
    }
    if (frame.kind === "quad") {
      const subject = frame.subject.value;
      const predicate = frame.predicate.value;
      const object = frame.object.value;
      const graph = frame.graph.value;
      if (!subject || !predicate || !object || !graph) {
        return { ok: false, reason: "invalid" };
      }
      const term = Object.freeze(rdf.quad(
        subject.term as Quad["subject"],
        predicate.term as Quad["predicate"],
        object.term as Quad["object"],
        graph.term as Quad["graph"],
      )) as Term;
      const snapshot = {
        term,
        quotedHeight: Math.max(
          subject.term.termType === "Quad" ? subject.quotedHeight + 1 : 0,
          object.term.termType === "Quad" ? object.quotedHeight + 1 : 0,
        ),
      };
      active.delete(frame.candidate);
      state.memo.set(frame.candidate, snapshot);
      frame.slot.value = snapshot;
      continue;
    }

    if (!isRecord(frame.candidate)) {
      return { ok: false, reason: "invalid" };
    }
    if (active.has(frame.candidate)) {
      return { ok: false, reason: "cycle" };
    }

    const memoized = state.memo.get(frame.candidate);
    if (memoized) {
      if (!frame.allowedTermTypes.includes(memoized.term.termType)) {
        return { ok: false, reason: "invalid" };
      }
      if (memoized.term.termType === "Quad") {
        if (
          frame.quotedDepth >
            UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxQuotedTermDepth ||
          frame.quotedDepth + memoized.quotedHeight >
            UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxQuotedTermDepth
        ) {
          return { ok: false, reason: "quoted-depth" };
        }
        if (frame.quotedDepth > 0) {
          if (nestedQuotedQuads.has(frame.candidate)) {
            return { ok: false, reason: "shared-quoted-quad" };
          }
          nestedQuotedQuads.add(frame.candidate);
        }
      }
      frame.slot.value = memoized;
      continue;
    }

    if (!state.counted.has(frame.candidate)) {
      state.counted.add(frame.candidate);
      state.termNodes += 1;
      if (
        state.termNodes >
        UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxTermNodes
      ) {
        return { ok: false, reason: "term-nodes" };
      }
    }

    const termType = frame.candidate.termType;
    const value = frame.candidate.value;
    const equals = frame.candidate.equals;
    if (
      typeof termType !== "string" ||
      !frame.allowedTermTypes.includes(termType) ||
      typeof value !== "string" ||
      typeof equals !== "function"
    ) {
      return { ok: false, reason: "invalid" };
    }

    let term: Term | undefined;
    switch (termType) {
      case "NamedNode":
        if (!isValidNamedNodeIri(value)) {
          return { ok: false, reason: "invalid" };
        }
        term = Object.freeze(rdf.namedNode(value)) as Term;
        break;
      case "BlankNode":
        term = Object.freeze(rdf.blankNode(value)) as Term;
        break;
      case "Variable":
        term = Object.freeze(rdf.variable(value)) as Term;
        break;
      case "DefaultGraph":
        if (value !== "") return { ok: false, reason: "invalid" };
        term = Object.freeze(rdf.defaultGraph()) as Term;
        break;
      case "Literal": {
        const language = frame.candidate.language;
        const datatype = frame.candidate.datatype;
        if (typeof language !== "string") {
          return { ok: false, reason: "invalid" };
        }
        const datatypeSlot: RdfTermSnapshotSlot = {};
        active.add(frame.candidate);
        stack.push({
          kind: "literal",
          candidate: frame.candidate,
          value,
          language,
          datatype: datatypeSlot,
          slot: frame.slot,
        });
        stack.push({
          kind: "enter",
          candidate: datatype,
          allowedTermTypes: ["NamedNode"],
          quotedDepth: frame.quotedDepth,
          slot: datatypeSlot,
        });
        continue;
      }
      case "Quad": {
        if (value !== "") return { ok: false, reason: "invalid" };
        if (
          frame.quotedDepth >
          UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxQuotedTermDepth
        ) {
          return { ok: false, reason: "quoted-depth" };
        }
        if (frame.quotedDepth > 0) {
          if (nestedQuotedQuads.has(frame.candidate)) {
            return { ok: false, reason: "shared-quoted-quad" };
          }
          nestedQuotedQuads.add(frame.candidate);
        }
        const subject = frame.candidate.subject;
        const predicate = frame.candidate.predicate;
        const object = frame.candidate.object;
        const graph = frame.candidate.graph;
        const subjectSlot: RdfTermSnapshotSlot = {};
        const predicateSlot: RdfTermSnapshotSlot = {};
        const objectSlot: RdfTermSnapshotSlot = {};
        const graphSlot: RdfTermSnapshotSlot = {};
        active.add(frame.candidate);
        stack.push({
          kind: "quad",
          candidate: frame.candidate,
          subject: subjectSlot,
          predicate: predicateSlot,
          object: objectSlot,
          graph: graphSlot,
          slot: frame.slot,
        });
        stack.push({
          kind: "enter",
          candidate: graph,
          allowedTermTypes: [
            "DefaultGraph",
            "NamedNode",
            "BlankNode",
            "Variable",
          ],
          quotedDepth: frame.quotedDepth,
          slot: graphSlot,
        });
        stack.push({
          kind: "enter",
          candidate: object,
          allowedTermTypes: [
            "NamedNode",
            "BlankNode",
            "Literal",
            "Variable",
            "Quad",
          ],
          quotedDepth: frame.quotedDepth + 1,
          slot: objectSlot,
        });
        stack.push({
          kind: "enter",
          candidate: predicate,
          allowedTermTypes: ["NamedNode", "Variable"],
          quotedDepth: frame.quotedDepth,
          slot: predicateSlot,
        });
        stack.push({
          kind: "enter",
          candidate: subject,
          allowedTermTypes: ["NamedNode", "BlankNode", "Variable", "Quad"],
          quotedDepth: frame.quotedDepth + 1,
          slot: subjectSlot,
        });
        continue;
      }
      default:
        return { ok: false, reason: "invalid" };
    }

    const snapshot = { term, quotedHeight: 0 };
    state.memo.set(frame.candidate, snapshot);
    frame.slot.value = snapshot;
  }

  return root.value
    ? { ok: true, value: root.value }
    : { ok: false, reason: "invalid" };
}

function snapshotRdfJsQuad(
  value: unknown,
  state: RdfTermSnapshotState,
): RdfTermSnapshotResult {
  return snapshotRdfTerm(value, ["Quad"], state);
}

function rdfInputFailure(
  path: string,
  message: string,
): UxApplicationLibraryAdmissionResult {
  return { ok: false, issues: [{ code: "rdf-input-invalid", path, message }] };
}

function closeIteratorSafely(iterator: Record<PropertyKey, unknown>): void {
  try {
    const close = iterator.return;
    if (typeof close !== "function") return;
    const result = Reflect.apply(close, iterator, []);
    if (isRecord(result)) {
      try {
        const then = result.then;
        if (typeof then === "function") {
          void Promise.resolve(result).catch(() => undefined);
        }
      } catch {
        // Iterator close failures never replace the primary typed input issue.
      }
    }
  } catch {
    // Iterator close failures never replace the primary typed input issue.
  }
}

function utf8ByteLengthUpTo(value: string, limit: number): number {
  let bytes = 0;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code <= 0x7f) {
      bytes += 1;
    } else if (code <= 0x7ff) {
      bytes += 2;
    } else if (
      code >= 0xd800 &&
      code <= 0xdbff &&
      index + 1 < value.length &&
      value.charCodeAt(index + 1) >= 0xdc00 &&
      value.charCodeAt(index + 1) <= 0xdfff
    ) {
      bytes += 4;
      index += 1;
    } else {
      bytes += 3;
    }
    if (bytes > limit) return limit + 1;
  }
  return bytes;
}

function rdfTermLexicalBytesUpTo(term: Term, limit: number): number {
  let bytes = 0;
  const add = (value: string): boolean => {
    const added = utf8ByteLengthUpTo(value, limit - bytes);
    bytes += added;
    return bytes <= limit;
  };
  const addTerm = (candidate: Term): boolean => {
    if (candidate.termType === "Quad") {
      return (
        addTerm(candidate.subject) &&
        addTerm(candidate.predicate) &&
        addTerm(candidate.object) &&
        addTerm(candidate.graph)
      );
    }
    if (!add(candidate.value)) return false;
    if (candidate.termType === "Literal") {
      return add(candidate.language) && add(candidate.datatype.value);
    }
    return true;
  };
  return addTerm(term) ? bytes : limit + 1;
}

function snapshotRdfIterable(
  quads: unknown,
):
  | { ok: true; value: Quad[] }
  | { ok: false; result: UxApplicationLibraryAdmissionResult } {
  let iterator: Record<PropertyKey, unknown>;
  let next: (...args: never[]) => unknown;
  try {
    if (quads === null || quads === undefined) {
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          "UX application library RDF input must be an iterable of RDFJS Quads.",
        ),
      };
    }
    const iterate = (quads as any)[Symbol.iterator];
    if (typeof iterate !== "function") {
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          "UX application library RDF input must be an iterable of RDFJS Quads.",
        ),
      };
    }
    const candidate = Reflect.apply(iterate, quads, []);
    if (!isRecord(candidate)) {
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          "UX application library RDF iterator must be an object.",
        ),
      };
    }
    iterator = candidate;
    const nextCandidate = iterator.next;
    if (typeof nextCandidate !== "function") {
      closeIteratorSafely(iterator);
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          "UX application library RDF iterator must provide next().",
        ),
      };
    }
    next = nextCandidate as (...args: never[]) => unknown;
  } catch (error) {
    return {
      ok: false,
      result: rdfInputFailure(
        "quads",
        `UX application library RDF input could not be read: ${safeThrownValue(error)}`,
      ),
    };
  }

  const snapshots: Quad[] = [];
  const termState: RdfTermSnapshotState = {
    counted: new WeakSet(),
    memo: new WeakMap(),
    termNodes: 0,
  };
  let lexicalBytes = 0;
  while (true) {
    let iteration: Record<PropertyKey, unknown>;
    try {
      const candidate = Reflect.apply(next, iterator, []);
      if (!isRecord(candidate)) {
        throw new TypeError("RDF iterator next() must return an object.");
      }
      iteration = candidate;
      if (iteration.done) return { ok: true, value: snapshots };
    } catch (error) {
      closeIteratorSafely(iterator);
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          `UX application library RDF input could not be read: ${safeThrownValue(error)}`,
        ),
      };
    }

    if (
      snapshots.length >=
      UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxQuads
    ) {
      closeIteratorSafely(iterator);
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          `UX application library RDF input exceeds the maximum quad count of ${UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxQuads}.`,
        ),
      };
    }

    let snapshotResult: RdfTermSnapshotResult;
    try {
      snapshotResult = snapshotRdfJsQuad(iteration.value, termState);
    } catch (error) {
      closeIteratorSafely(iterator);
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          `UX application library RDF input validation failed: ${safeThrownValue(error)}`,
        ),
      };
    }
    if (!snapshotResult.ok) {
      closeIteratorSafely(iterator);
      const path =
        snapshotResult.reason === "term-nodes"
          ? "quads"
          : `quads[${snapshots.length}]`;
      const messageByReason: Record<RdfTermSnapshotFailure, string> = {
        invalid:
          "UX application library RDF input must contain complete RDFJS Quad values.",
        cycle:
          "UX application library RDF input contains a cyclic RDFJS term graph.",
        "shared-quoted-quad":
          "UX application library RDF input must not reuse one RDFJS term object identity within a quad graph.",
        "quoted-depth":
          `UX application library RDF input exceeds the maximum quoted Quad depth of ${UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxQuotedTermDepth}.`,
        "term-nodes":
          `UX application library RDF input exceeds the maximum distinct RDFJS term node count of ${UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxTermNodes}.`,
      };
      return {
        ok: false,
        result: rdfInputFailure(
          path,
          messageByReason[snapshotResult.reason],
        ),
      };
    }
    const snapshot = snapshotResult.value.term as Quad;

    const remainingLexicalBytes =
      UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxTermLexicalBytes -
      lexicalBytes;
    const candidateLexicalBytes = rdfTermLexicalBytesUpTo(
      snapshot,
      remainingLexicalBytes,
    );
    if (candidateLexicalBytes > remainingLexicalBytes) {
      closeIteratorSafely(iterator);
      return {
        ok: false,
        result: rdfInputFailure(
          "quads",
          `UX application library RDF input exceeds the maximum aggregate term lexical byte count of ${UX_APPLICATION_LIBRARY_RDF_INPUT_BUDGETS.maxTermLexicalBytes}.`,
        ),
      };
    }
    lexicalBytes += candidateLexicalBytes;
    snapshots.push(snapshot);
  }
}

function shaclMessage(result: any): string {
  const resultMessage = result.message;
  const messages = Array.isArray(resultMessage)
    ? resultMessage
    : resultMessage
      ? [resultMessage]
      : [];
  const rendered = messages
    .map((candidate: any) => {
      const value = candidate?.value;
      return typeof value === "string"
        ? value
        : typeof candidate === "string"
          ? candidate
          : "";
    })
    .filter(Boolean);
  return (
    rendered.join("; ") || "UX application library SHACL constraint failed."
  );
}

function isPresentationPath(path: string): boolean {
  return (
    /(class|style|color|coordinate|position|width|height|renderer|figma|padding|spacing|typography)/i.test(
      path,
    ) || /(?:^|[#/])(?:x|y|dx|dy)$/i.test(path)
  );
}

function isValidNamedNodeIri(value: string): boolean {
  if (
    value.length === 0 ||
    /[\u0000-\u0020<>"{}|\\^`]/u.test(value) ||
    /%(?![0-9a-f]{2})/iu.test(value) ||
    !/^[a-z][a-z0-9+.-]*:/iu.test(value)
  ) {
    return false;
  }
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

function lengthDelimited(value: string): string {
  return `${value.length}:${value}`;
}

function typedTermKey(termType: string, fields: readonly string[]): string {
  return `${lengthDelimited(termType)}${fields
    .map((field) => lengthDelimited(field))
    .join("")}`;
}

function graphTermKey(term: Term): string {
  switch (term.termType) {
    case "Literal":
      return typedTermKey("Literal", [
        graphTermKey(term.datatype),
        term.language,
        term.value,
      ]);
    case "Quad":
      return typedTermKey("Quad", [
        graphTermKey(term.subject),
        graphTermKey(term.predicate),
        graphTermKey(term.object),
        graphTermKey(term.graph),
      ]);
    default:
      return typedTermKey(term.termType, [term.value]);
  }
}

function graphTermHandle(term: Term): string {
  return term.termType === "NamedNode" ? term.value : graphTermKey(term);
}

class Graph {
  private readonly termsBySubjectAndPredicate: ReadonlyMap<
    string,
    ReadonlyMap<string, readonly Quad["object"][]>
  >;
  private readonly subjectsByType: ReadonlyMap<string, readonly string[]>;
  private readonly typesBySubject: ReadonlyMap<string, ReadonlySet<string>>;
  private readonly subjectKeyByHandle: ReadonlyMap<string, string>;

  constructor(quads: readonly Quad[]) {
    const mutableTerms = new Map<
      string,
      Map<string, Map<string, Quad["object"]>>
    >();
    const mutableSubjectsByType = new Map<string, Set<string>>();
    const mutableTypesBySubject = new Map<string, Set<string>>();
    const mutableSubjectKeyByHandle = new Map<string, string>();

    for (const candidate of quads) {
      if (candidate.predicate.termType !== "NamedNode") continue;
      const subject = graphTermKey(candidate.subject);
      const subjectHandle = graphTermHandle(candidate.subject);
      mutableSubjectKeyByHandle.set(subjectHandle, subject);
      const predicate = candidate.predicate.value;
      const predicates = mutableTerms.get(subject) ?? new Map();
      const objects = predicates.get(predicate) ?? new Map();
      objects.set(graphTermKey(candidate.object), candidate.object);
      predicates.set(predicate, objects);
      mutableTerms.set(subject, predicates);

      if (
        predicate === RDF_TYPE &&
        candidate.object.termType === "NamedNode"
      ) {
        const classRef = candidate.object.value;
        const subjects = mutableSubjectsByType.get(classRef) ?? new Set();
        subjects.add(subjectHandle);
        mutableSubjectsByType.set(classRef, subjects);
        const types = mutableTypesBySubject.get(subject) ?? new Set();
        types.add(classRef);
        mutableTypesBySubject.set(subject, types);
      }
    }

    this.termsBySubjectAndPredicate = new Map(
      [...mutableTerms].map(([subject, predicates]) => [
        subject,
        new Map(
          [...predicates].map(([predicate, objects]) => [
            predicate,
            Object.freeze([...objects.values()]),
          ]),
        ),
      ]),
    );
    this.subjectsByType = new Map(
      [...mutableSubjectsByType].map(([classRef, subjects]) => [
        classRef,
        Object.freeze([...subjects].sort()),
      ]),
    );
    this.typesBySubject = mutableTypesBySubject;
    this.subjectKeyByHandle = mutableSubjectKeyByHandle;
  }

  terms(subject: string, predicate: string): Quad["object"][] {
    const subjectKey = this.subjectKeyByHandle.get(subject);
    if (!subjectKey) return [];
    return [
      ...(this.termsBySubjectAndPredicate
        .get(subjectKey)
        ?.get(p(predicate)) ?? []),
    ];
  }

  refs(subject: string, predicate: string): string[] {
    return this.terms(subject, predicate).map(graphTermHandle).sort();
  }

  values(subject: string, predicate: string): string[] {
    return [
      ...new Set(
        this.terms(subject, predicate).map((term) => term.value),
      ),
    ].sort();
  }

  one(subject: string, predicate: string): string | undefined {
    const values = this.values(subject, predicate);
    return values.length === 1 ? values[0] : undefined;
  }

  typed(classRef: string): string[] {
    return [...(this.subjectsByType.get(classRef) ?? [])];
  }

  hasType(subject: string, classRef: string): boolean {
    const subjectKey = this.subjectKeyByHandle.get(subject);
    return subjectKey
      ? (this.typesBySubject.get(subjectKey)?.has(classRef) ?? false)
      : false;
  }

  boolean(subject: string, predicate: string): boolean | undefined {
    const terms = this.terms(subject, predicate);
    if (terms.length !== 1) return undefined;
    const term = terms[0]!;
    if (
      term.termType !== "Literal" ||
      term.language !== "" ||
      term.datatype.value !== `${XSD}boolean`
    ) {
      return undefined;
    }
    if (term.value === "true" || term.value === "1") return true;
    if (term.value === "false" || term.value === "0") return false;
    return undefined;
  }
}

function issue(
  code: UxApplicationLibraryAdmissionIssueCode,
  path: string,
  message: string,
  focusNode?: string,
): UxApplicationLibraryAdmissionIssue {
  return {
    code,
    path,
    message,
    ...(focusNode ? { focusNode } : {}),
  };
}

function sourceRefMatchesKind(
  kind: string | undefined,
  sourceRef: string | undefined,
): boolean {
  if (!kind || !sourceRef) return false;
  const patternByKind: Record<string, RegExp> = {
    "admitted-ux-research":
      /^cogo:ux-observation\/[a-z0-9][a-z0-9-]*$/,
    "application-core":
      /^cogo:application-core\/[a-z0-9][a-z0-9-]*$/,
    "human-authored-canonical":
      /^cogo:approved-source\/[a-z0-9][a-z0-9-]*$/,
  };
  return patternByKind[kind]?.test(sourceRef) ?? false;
}

function authorityRefsForKind(
  authority: AuthoritySnapshot,
  kind: string | undefined,
): readonly string[] {
  switch (kind) {
    case "admitted-ux-research":
      return authority.admittedUxResearchRefs;
    case "human-authored-canonical":
      return authority.approvedCanonicalSourceRefs;
    case "application-core":
      return authority.admittedApplicationCoreRefs;
    default:
      return [];
  }
}

function addDuplicateIssues(
  graph: Graph,
  resources: string[],
  idPredicate: string,
  code: UxApplicationLibraryAdmissionIssueCode,
  path: string,
  owner: string,
  issues: UxApplicationLibraryAdmissionIssue[],
) {
  const byId = new Map<string, string[]>();
  for (const resource of resources) {
    const id = graph.one(resource, idPredicate);
    if (!id) continue;
    const refs = byId.get(id) ?? [];
    refs.push(resource);
    byId.set(id, refs);
  }
  for (const [id, refs] of [...byId].sort(([left], [right]) =>
    left.localeCompare(right),
  )) {
    if (refs.length < 2) continue;
    issues.push(
      issue(
        code,
        path,
        `Duplicate ${idPredicate} "${id}" in exact owner ${owner}: ${refs.sort().join(", ")}.`,
        owner,
      ),
    );
  }
}

function validateSourceAuthority(
  graph: Graph,
  subject: string,
  authorityPredicate: string,
  authority: AuthoritySnapshot,
  issues: UxApplicationLibraryAdmissionIssue[],
) {
  const authorityNodes = graph.refs(subject, authorityPredicate);
  const subjectKind = graph.one(subject, "sourceKind");
  const subjectRefs = graph.values(subject, "sourceRef");
  const subjectStatus = graph.one(subject, "authorityStatus");
  const admittedRefs = new Set(authorityRefsForKind(authority, subjectKind));
  const matchedAuthorities = new Set<string>();
  let valid =
    subjectRefs.length > 0 &&
    subjectRefs.length === authorityNodes.length &&
    subjectStatus === "consumer-verified";
  for (const sourceRef of subjectRefs) {
    const matches = authorityNodes.filter(
      (authorityNode) =>
        authorityNode !== subject &&
        graph.hasType(authorityNode, classes.sourceAuthority) &&
        graph.one(authorityNode, "sourceKind") === subjectKind &&
        graph.one(authorityNode, "sourceRef") === sourceRef &&
        graph.one(authorityNode, "authorityStatus") === subjectStatus &&
        sourceRefMatchesKind(subjectKind, sourceRef) &&
        admittedRefs.has(sourceRef),
    );
    if (matches.length !== 1) {
      valid = false;
    } else {
      matchedAuthorities.add(matches[0]!);
    }
  }
  if (matchedAuthorities.size !== authorityNodes.length) valid = false;
  if (!valid) {
    issues.push(
      issue(
        "library-source-forbidden",
        p(authorityPredicate),
        `Source authority for ${subject} must be externally admitted, independent, typed, and exactly agree one-to-one on kind/ref/status.`,
        subject,
      ),
    );
  }
}

const semanticContentPredicates = [
  "contentTitle",
  "contentDescription",
  "contentLabel",
  "contentLocation",
  "contentPendingLabel",
  "contentSuccessLabel",
  "contentErrorLabel",
] as const;

function isJsonSemanticLiteral(term: Quad["object"]): boolean {
  if (term.termType !== "Literal" || term.language !== "") return false;
  switch (term.datatype.value) {
    case `${XSD}string`:
      return true;
    case `${XSD}boolean`:
      return ["true", "false", "1", "0"].includes(term.value);
    case `${XSD}integer`:
      return /^[+-]?[0-9]+$/.test(term.value);
    case `${XSD}decimal`:
      return /^[+-]?(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)$/.test(term.value);
    case `${XSD}double`:
      return Number.isFinite(Number(term.value));
    default:
      return false;
  }
}

function validateDefinition(
  graph: Graph,
  definition: string,
  authority: AuthoritySnapshot,
  issues: UxApplicationLibraryAdmissionIssue[],
) {
  validateSourceAuthority(
    graph,
    definition,
    "hasSourceAuthority",
    authority,
    issues,
  );
  const states = graph.refs(definition, "hasState");
  const transitions = graph.refs(definition, "hasTransition");
  const ports = graph.refs(definition, "hasPort");
  const nodes = graph.refs(definition, "hasSemanticNode");
  addDuplicateIssues(
    graph,
    states,
    "stateId",
    "exact-namespace-collision",
    `${definition}#hasState`,
    definition,
    issues,
  );
  addDuplicateIssues(
    graph,
    transitions,
    "transitionId",
    "exact-namespace-collision",
    `${definition}#hasTransition`,
    definition,
    issues,
  );
  addDuplicateIssues(
    graph,
    ports,
    "portId",
    "duplicate-library-port-id",
    `${definition}#hasPort`,
    definition,
    issues,
  );
  addDuplicateIssues(
    graph,
    nodes,
    "nodeId",
    "exact-namespace-collision",
    `${definition}#hasSemanticNode`,
    definition,
    issues,
  );
  const stateIds = new Set(
    states.flatMap((state) => graph.values(state, "stateId")),
  );
  const portById = new Map(
    ports.flatMap((port) => {
      const portId = graph.one(port, "portId");
      return portId ? [[portId, port] as const] : [];
    }),
  );
  for (const transition of transitions) {
    const fromState = graph.one(transition, "fromState");
    const toState = graph.one(transition, "toState");
    if (
      (fromState && !stateIds.has(fromState)) ||
      (toState && !stateIds.has(toState))
    ) {
      issues.push(
        issue(
          "ux-pattern-transition-invalid",
          p("toState"),
          "Transition states must resolve exactly in the owning UX definition.",
          transition,
        ),
      );
    }
    const eventRef = graph.one(transition, "eventRef");
    const eventPort = eventRef ? portById.get(eventRef) : undefined;
    if (
      eventRef &&
      (!eventPort ||
        graph.one(eventPort, "portKind") !== "event" ||
        graph.one(eventPort, "portDirection") !== "in")
    ) {
      issues.push(
        issue(
          "ux-pattern-transition-invalid",
          p("eventRef"),
          "Transition eventRef must resolve exactly to an event/in port.",
          transition,
        ),
      );
    }
    const effects = graph.values(transition, "effectRef");
    if (
      effects.some((effectRef) => !idPattern.test(effectRef)) ||
      Number(graph.one(transition, "effectRefCount")) !== effects.length
    ) {
      issues.push(
        issue(
          "ux-pattern-transition-invalid",
          p("effectRef"),
          "Transition effect refs must be exact IDs and agree with effectRefCount.",
          transition,
        ),
      );
    }
    const emittedRefs = graph.values(transition, "emitEventRef");
    if (
      Number(graph.one(transition, "emitEventRefCount")) !== emittedRefs.length
    ) {
      issues.push(
        issue(
          "ux-pattern-transition-invalid",
          p("emitEventRef"),
          "Transition emitted event refs must agree with emitEventRefCount.",
          transition,
        ),
      );
    }
    for (const emittedRef of emittedRefs) {
      const emittedPort = portById.get(emittedRef);
      if (
        !emittedPort ||
        graph.one(emittedPort, "portKind") !== "event" ||
        graph.one(emittedPort, "portDirection") !== "out"
      ) {
        issues.push(
          issue(
            "ux-pattern-transition-invalid",
            p("emitEventRef"),
            "Emitted events must resolve exactly to event/out ports.",
            transition,
          ),
        );
      }
    }
  }
  const capabilities = new Set(graph.values(definition, "requiredCapability"));
  for (const port of ports) {
    if (graph.boolean(port, "portRequired") === undefined) {
      issues.push(
        issue(
          "shacl-conformance-failed",
          p("portRequired"),
          "port required metadata must use a valid xsd:boolean lexical form",
          port,
        ),
      );
    }
  }
  for (const node of nodes) {
    for (const predicate of semanticContentPredicates) {
      if (
        graph
          .terms(node, predicate)
          .some((term) => !isJsonSemanticLiteral(term))
      ) {
        issues.push(
          issue(
            "shacl-conformance-failed",
            p(predicate),
            "Semantic content must preserve a JSON string, number, or boolean literal.",
            node,
          ),
        );
      }
    }
    if (
      graph
        .values(node, "visibleIn")
        .some((stateId) => !stateIds.has(stateId))
    ) {
      issues.push(
        issue(
          "ux-pattern-state-invalid",
          p("visibleIn"),
          "Semantic visible states must resolve exactly in the owning UX definition.",
          node,
        ),
      );
    }
    const actionCapability = graph.one(node, "actionCapabilityRef");
    if (actionCapability && !capabilities.has(actionCapability)) {
      issues.push(
        issue(
          "library-capability-invalid",
          p("actionCapabilityRef"),
          "Semantic action capability must resolve in requiredCapabilities.",
          node,
        ),
      );
    }
  }
}

type ResolvedPort = {
  key: string;
  resource: string;
  required: boolean | undefined;
  kind?: string;
  direction?: string;
  valueType?: string;
};

function portRecord(
  graph: Graph,
  key: string,
  resource: string,
): ResolvedPort {
  return {
    key,
    resource,
    required: graph.boolean(resource, "portRequired"),
    kind: graph.one(resource, "portKind"),
    direction: graph.one(resource, "portDirection"),
    valueType: graph.one(resource, "portValueType"),
  };
}

function traverseJourney(
  steps: Map<string, string>,
  graph: Graph,
  entryStepId: string,
  includeRecovery: boolean,
): Set<string> {
  const reachable = new Set<string>();
  if (!steps.has(entryStepId)) return reachable;
  const queue = [entryStepId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (reachable.has(current)) continue;
    reachable.add(current);
    const step = steps.get(current);
    if (!step) continue;
    for (const transition of graph.refs(step, "hasJourneyTransition")) {
      const path = graph.one(transition, "journeyPath");
      const target = graph.one(transition, "toStepId");
      if (
        target &&
        steps.has(target) &&
        (includeRecovery || path !== "recovery")
      ) {
        queue.push(target);
      }
    }
  }
  return reachable;
}

function hasNonRecoveryResolution(
  steps: Map<string, string>,
  graph: Graph,
  targetId: string,
  independentlyReachable: Set<string>,
  terminalIds: Set<string>,
): boolean {
  const target = steps.get(targetId);
  if (!target) return false;
  if (
    independentlyReachable.has(targetId) ||
    terminalIds.has(targetId)
  ) {
    return true;
  }
  const queue = graph
    .values(target, "hasJourneyTransition")
    .filter(
      (transition) => graph.one(transition, "journeyPath") !== "recovery",
    )
    .flatMap((transition) => graph.values(transition, "toStepId"));
  const visited = new Set<string>();
  while (queue.length > 0) {
    const stepId = queue.shift()!;
    if (visited.has(stepId)) continue;
    visited.add(stepId);
    if (independentlyReachable.has(stepId) || terminalIds.has(stepId)) {
      return true;
    }
    const step = steps.get(stepId);
    if (!step) continue;
    for (const transition of graph.refs(step, "hasJourneyTransition")) {
      if (graph.one(transition, "journeyPath") === "recovery") continue;
      queue.push(...graph.values(transition, "toStepId"));
    }
  }
  return false;
}

function validateApplication(
  graph: Graph,
  application: string,
  authority: AuthoritySnapshot,
  issues: UxApplicationLibraryAdmissionIssue[],
) {
  validateSourceAuthority(
    graph,
    application,
    "hasSourceAuthority",
    authority,
    issues,
  );
  const coreAuthorities = graph.refs(
    application,
    "hasApplicationCoreAuthority",
  );
  const coreAuthority =
    coreAuthorities.length === 1 ? coreAuthorities[0] : undefined;
  const applicationCoreRef = graph.one(application, "applicationCoreRef");
  const applicationSourceKind = graph.one(application, "sourceKind");
  const applicationSourceRefs = graph.values(application, "sourceRef");
  const applicationSourceAuthorities = graph.refs(
    application,
    "hasSourceAuthority",
  );
  const exactCoreLineage =
    applicationSourceKind !== "application-core" ||
    (applicationSourceRefs.length === 1 &&
      applicationSourceRefs[0] === applicationCoreRef &&
      applicationSourceAuthorities.length === 1 &&
      applicationSourceAuthorities[0] === coreAuthority);
  if (
    !coreAuthority ||
    coreAuthority === application ||
    !graph.hasType(coreAuthority, classes.applicationCoreAuthority) ||
    !graph.hasType(coreAuthority, classes.sourceAuthority) ||
    graph.one(coreAuthority, "sourceKind") !== "application-core" ||
    graph.one(coreAuthority, "authorityStatus") !== "consumer-verified" ||
    graph.one(coreAuthority, "sourceRef") !== applicationCoreRef ||
    !authority.admittedApplicationCoreRefs.includes(
      applicationCoreRef ?? "",
    ) ||
    !exactCoreLineage
  ) {
    issues.push(
      issue(
        "library-source-forbidden",
        p("hasApplicationCoreAuthority"),
        "Application lineage and Application Core must resolve to one independently admitted exact authority.",
        application,
      ),
    );
  }

  const capabilities = new Set(graph.values(application, "capability"));
  const applicationEvents = new Set(
    graph.values(application, "applicationEvent"),
  );
  const applicationPorts = graph.refs(application, "hasPort");
  for (const port of applicationPorts) {
    const portId = graph.one(port, "portId");
    if (
      graph.one(port, "portKind") === "event" &&
      portId &&
      !applicationEvents.has(portId)
    ) {
      issues.push(
        issue(
          "library-event-invalid",
          p("portId"),
          "Application event portId must resolve to an exact application event.",
          port,
        ),
      );
    }
    if (graph.boolean(port, "portRequired") === undefined) {
      issues.push(
        issue(
          "shacl-conformance-failed",
          p("portRequired"),
          "port required metadata must use a valid xsd:boolean lexical form",
          port,
        ),
      );
    }
  }
  addDuplicateIssues(
    graph,
    applicationPorts,
    "portId",
    "duplicate-library-port-id",
    `${application}#hasPort`,
    application,
    issues,
  );

  // `webMcpToolRefCount` is the empty-vs-absent discriminator for the static
  // capability declaration — the same job `effectRefCount` / `emitEventRefCount`
  // do for transitions and `uxInstanceCount` does for journey steps. SHACL Core
  // cannot compare a count literal against a property's cardinality, so the gate
  // must, and without this a shard whose count disagrees with its refs is
  // admitted with zero issues while the JSON round-trip silently breaks. The
  // gate has to stand on its own: a mechanically produced designation may have
  // no mirror fixture behind it.
  //
  // `graph.values` is the set view, matching the precedent above — and that is
  // also why tool-ref uniqueness needs no separate check here: a repeated
  // identical literal is ONE triple in RDF, so a shard that lists a ref twice
  // and declares a count of 2 is already rejected by this comparison.
  for (const capabilities of graph.refs(application, "hasRuntimeCapabilities")) {
    for (const webMcp of graph.refs(capabilities, "hasWebMcp")) {
      const toolRefs = graph.values(webMcp, "webMcpToolRef");
      if (Number(graph.one(webMcp, "webMcpToolRefCount")) !== toolRefs.length) {
        issues.push(
          issue(
            "application-capability-invalid",
            p("webMcpToolRef"),
            "Application WebMCP tool refs must agree with webMcpToolRefCount.",
            webMcp,
          ),
        );
      }
    }
  }

  const flows = graph.refs(application, "hasApplicationFlow");
  addDuplicateIssues(
    graph,
    flows,
    "flowId",
    "exact-namespace-collision",
    `${application}#hasApplicationFlow`,
    application,
    issues,
  );
  for (const flow of flows) {
    const eventRef = graph.one(flow, "eventRef");
    const capabilityRef = graph.one(flow, "capabilityRef");
    if (eventRef && !applicationEvents.has(eventRef)) {
      issues.push(
        issue(
          "library-event-invalid",
          p("eventRef"),
          "Application flow event ref must resolve exactly.",
          flow,
        ),
      );
    }
    if (capabilityRef && !capabilities.has(capabilityRef)) {
      issues.push(
        issue(
          "library-capability-invalid",
          p("capabilityRef"),
          "Application flow capability ref must resolve exactly.",
          flow,
        ),
      );
    }
  }
  const datasources = graph.refs(application, "hasDatasourceRequirement");
  addDuplicateIssues(
    graph,
    datasources,
    "datasourceId",
    "exact-namespace-collision",
    `${application}#hasDatasourceRequirement`,
    application,
    issues,
  );
  for (const datasource of datasources) {
    const capabilityRef = graph.one(datasource, "capabilityRef");
    if (capabilityRef && !capabilities.has(capabilityRef)) {
      issues.push(
        issue(
          "library-capability-invalid",
          p("capabilityRef"),
          "Datasource capability ref must resolve exactly.",
          datasource,
        ),
      );
    }
  }

  const definitionByRef = new Map<string, string>();
  for (const definition of graph.typed(classes.definition)) {
    const id = graph.one(definition, "uxPatternId");
    const version = graph.one(definition, "version");
    const definitionRef = id && version ? `${id}@${version}` : undefined;
    if (definitionRef && !definitionByRef.has(definitionRef)) {
      definitionByRef.set(definitionRef, definition);
    }
  }
  const instances = graph.refs(application, "hasUxInstance");
  addDuplicateIssues(
    graph,
    instances,
    "uxInstanceId",
    "duplicate-ux-instance-id",
    `${application}#hasUxInstance`,
    application,
    issues,
  );
  const instanceById = new Map<string, { resource: string; definition: string }>();
  const applicationBlueprintId = graph.one(application, "applicationBlueprintId");
  const applicationInstanceId = graph.one(application, "applicationInstanceId");
  for (const instance of instances) {
    const id = graph.one(instance, "uxInstanceId");
    const definitionRef = graph.one(instance, "definitionRef");
    const definitionTargets = graph.refs(instance, "hasDefinition");
    const expectedTarget = definitionRef
      ? definitionByRef.get(definitionRef)
      : undefined;
    if (
      !definitionRef ||
      !expectedTarget ||
      definitionTargets.length !== 1 ||
      definitionTargets[0] !== expectedTarget
    ) {
      issues.push(
        issue(
          "definition-reference-invalid",
          p("definitionRef"),
          "Definition reference must agree with its exact id@version target.",
          instance,
        ),
      );
      continue;
    }
    if (id) instanceById.set(id, { resource: instance, definition: expectedTarget });
    const expectedNamespace =
      id && applicationBlueprintId && applicationInstanceId
        ? `cogo://application-blueprint/${applicationBlueprintId}/${applicationInstanceId}/ux/${id}`
        : undefined;
    if (
      expectedNamespace &&
      graph.one(instance, "exactNamespace") !== expectedNamespace
    ) {
      issues.push(
        issue(
          "exact-namespace-invalid",
          p("exactNamespace"),
          `Exact namespace for ${instance} must be ${expectedNamespace}.`,
          instance,
        ),
      );
    }
  }

  const appPortById = new Map<string, ResolvedPort>();
  for (const resource of applicationPorts) {
    const id = graph.one(resource, "portId");
    if (id) appPortById.set(id, portRecord(graph, `application:${id}`, resource));
  }
  const instancePortById = new Map<string, ResolvedPort>();
  for (const [instanceId, instance] of instanceById) {
    for (const resource of graph.refs(instance.definition, "hasPort")) {
      const id = graph.one(resource, "portId");
      if (id) {
        const key = `ux-instance:${instanceId}:${id}`;
        instancePortById.set(key, portRecord(graph, key, resource));
      }
    }
  }
  const resolveEndpoint = (
    binding: string,
    side: "source" | "target",
  ): ResolvedPort | undefined => {
    const scope = graph.one(binding, `${side}Scope`);
    const portId = graph.one(binding, `${side}PortId`);
    const uxInstanceId = graph.one(binding, `${side}UxInstanceId`);
    if (!portId) return undefined;
    if (scope === "application" && !uxInstanceId) return appPortById.get(portId);
    if (scope === "ux-instance" && uxInstanceId) {
      return instancePortById.get(`ux-instance:${uxInstanceId}:${portId}`);
    }
    return undefined;
  };

  const bindings = graph.refs(application, "hasBinding");
  addDuplicateIssues(
    graph,
    bindings,
    "bindingId",
    "exact-namespace-collision",
    `${application}#hasBinding`,
    application,
    issues,
  );
  const participation = new Map<string, number>();
  for (const binding of bindings) {
    const source = resolveEndpoint(binding, "source");
    const target = resolveEndpoint(binding, "target");
    if (!source || !target) {
      issues.push(
        issue(
          "incompatible-pattern-port",
          binding,
          "Binding endpoints must resolve by exact scope, instance, and port ID.",
          binding,
        ),
      );
      continue;
    }
    if (
      source.direction !== "out" ||
      target.direction !== "in" ||
      source.kind !== target.kind ||
      source.valueType !== target.valueType
    ) {
      issues.push(
        issue(
          "incompatible-pattern-port",
          binding,
          "Binding direction, kind, and valueType must match exactly.",
          binding,
        ),
      );
    }
    participation.set(source.key, (participation.get(source.key) ?? 0) + 1);
    participation.set(target.key, (participation.get(target.key) ?? 0) + 1);
  }
  for (const port of [...appPortById.values(), ...instancePortById.values()]) {
    if (port.required === true && participation.get(port.key) !== 1) {
      issues.push(
        issue(
          "unbound-pattern-port",
          port.resource,
          "Every required port must participate in exactly one binding.",
          port.resource,
        ),
      );
    }
  }

  const journeys = graph.refs(application, "hasJourney");
  const journey = journeys.length === 1 ? journeys[0] : undefined;
  if (!journey) return;
  const stepResources = graph.refs(journey, "hasStep");
  addDuplicateIssues(
    graph,
    stepResources,
    "stepId",
    "exact-namespace-collision",
    `${journey}#hasStep`,
    journey,
    issues,
  );
  const steps = new Map<string, string>();
  const ownedInstances = new Set<string>();
  for (const step of stepResources) {
    const stepId = graph.one(step, "stepId");
    if (stepId) steps.set(stepId, step);
    const stepInstances = graph.refs(step, "hasUxInstance");
    const transitions = graph.refs(step, "hasJourneyTransition");
    if (
      Number(graph.one(step, "uxInstanceCount")) !== stepInstances.length ||
      Number(graph.one(step, "journeyTransitionCount")) !== transitions.length
    ) {
      issues.push(
        issue(
          "journey-step-unreachable",
          step,
          "Journey step ownership and transition counts must match the explicit graph.",
          step,
        ),
      );
    }
    for (const instance of stepInstances) {
      if (!instances.includes(instance)) {
        issues.push(
          issue(
            "journey-step-unreachable",
            p("hasUxInstance"),
            "Journey step instance ownership must resolve exactly.",
            step,
          ),
        );
      } else {
        ownedInstances.add(instance);
      }
    }
  }
  for (const instance of instances) {
    if (!ownedInstances.has(instance)) {
      issues.push(
        issue(
          "journey-step-unreachable",
          p("hasUxInstance"),
          "Every UX instance must be owned by at least one journey step.",
          instance,
        ),
      );
    }
  }
  const entry = graph.one(journey, "entryStepId");
  if (entry && !steps.has(entry)) {
    issues.push(
      issue(
        "journey-entry-invalid",
        p("entryStepId"),
        "Journey entry step must resolve exactly.",
        journey,
      ),
    );
  }
  const terminalIds = new Set(graph.values(journey, "terminalStepId"));
  for (const terminal of terminalIds) {
    if (!steps.has(terminal)) {
      issues.push(
        issue(
          "journey-terminal-missing",
          p("terminalStepId"),
          "Journey terminal step must resolve exactly.",
          journey,
        ),
      );
    }
  }
  for (const step of stepResources) {
    for (const transition of graph.refs(step, "hasJourneyTransition")) {
      const eventRef = graph.one(transition, "journeyEventRef");
      const target = graph.one(transition, "toStepId");
      const path = graph.one(transition, "journeyPath");
      if (eventRef && !applicationEvents.has(eventRef)) {
        issues.push(
          issue(
            "ux-pattern-transition-invalid",
            p("journeyEventRef"),
            "Journey event must resolve to an application event.",
            transition,
          ),
        );
      }
      if (target && !steps.has(target)) {
        issues.push(
          issue(
            path === "recovery"
              ? "journey-recovery-invalid"
              : "journey-step-unreachable",
            p("toStepId"),
            "Journey transition target must resolve exactly.",
            transition,
          ),
        );
      }
    }
  }
  const reachableWithoutRecovery = traverseJourney(
    steps,
    graph,
    entry ?? "",
    false,
  );
  const reachable = traverseJourney(steps, graph, entry ?? "", true);
  for (const [stepId, step] of steps) {
    if (!reachable.has(stepId)) {
      issues.push(
        issue(
          "journey-step-unreachable",
          step,
          "Journey step must be reachable from entry.",
          step,
        ),
      );
    }
    for (const transition of graph.refs(step, "hasJourneyTransition")) {
      if (graph.one(transition, "journeyPath") !== "recovery") continue;
      const target = graph.one(transition, "toStepId");
      if (
        !target ||
        !reachableWithoutRecovery.has(stepId) ||
        !hasNonRecoveryResolution(
          steps,
          graph,
          target,
          reachableWithoutRecovery,
          terminalIds,
        )
      ) {
        issues.push(
          issue(
            "journey-recovery-invalid",
            p("journeyPath"),
            "Recovery transitions must leave a reachable source and return through a valid non-recovery resolution.",
            transition,
          ),
        );
      }
    }
  }
  for (const terminal of terminalIds) {
    if (!reachable.has(terminal)) {
      issues.push(
        issue(
          "journey-terminal-missing",
          p("terminalStepId"),
          "Journey terminal step must be reachable.",
          journey,
        ),
      );
    }
  }
}

function dedupeAndSort(
  issues: UxApplicationLibraryAdmissionIssue[],
): UxApplicationLibraryAdmissionIssue[] {
  return [
    ...new Map(
      issues.map((candidate) => [
        [
          candidate.code,
          candidate.path,
          candidate.message,
          candidate.focusNode ?? "",
        ].join("\u0000"),
        candidate,
      ]),
    ).values(),
  ].sort((left, right) =>
    [left.code, left.path, left.message, left.focusNode ?? ""]
      .join("\u0000")
      .localeCompare(
        [right.code, right.path, right.message, right.focusNode ?? ""].join(
          "\u0000",
        ),
      ),
  );
}

/**
 * Sole public UX/Application Library ontology admission gate. SHACL enforces
 * the closed field contract; deterministic graph checks enforce exact
 * cross-resource identity, authority, port, statechart, and journey semantics.
 */
export async function admitUxApplicationLibraryOntology(
  quads: Iterable<Quad>,
  authorityContext: unknown,
): Promise<UxApplicationLibraryAdmissionResult> {
  const authority = snapshotAuthorityContext(authorityContext);
  if (!authority.ok) {
    return {
      ok: false,
      issues: [
        issue(
          "authority-context-invalid",
          "authorityContext",
          authority.message,
        ),
      ],
    };
  }

  const snapshot = snapshotRdfIterable(quads);
  if (!snapshot.ok) return snapshot.result;
  const materialized = snapshot.value;
  const dataset = rdf.dataset();
  try {
    for (const candidate of materialized) dataset.add(candidate);
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `UX application library RDF input could not be loaded: ${safeThrownValue(error)}`,
    );
  }

  let report: any;
  try {
    const validator = new SHACLValidator(parseDataset(canonicalShapesTtl), {
      importGraph: async () => rdf.dataset(),
    });
    report = await validator.validate(dataset);
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `UX application library RDF validation could not run: ${safeThrownValue(error)}`,
    );
  }

  let issues: UxApplicationLibraryAdmissionIssue[];
  try {
    const results = report.results;
    if (!Array.isArray(results)) {
      throw new TypeError("SHACL report results must be an array.");
    }
    issues = results.map((result: any) => {
      const message = shaclMessage(result);
      const pathValue = result.path?.value;
      const focusValue = result.focusNode?.value;
      const path = typeof pathValue === "string" ? pathValue : "";
      return {
        code: message.includes("library-source-forbidden")
          ? "library-source-forbidden"
          : isPresentationPath(path)
            ? "library-presentation-forbidden"
            : "shacl-conformance-failed",
        path,
        message,
        ...(typeof focusValue === "string" && focusValue
          ? { focusNode: focusValue }
          : {}),
      };
    });
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `UX application library SHACL report could not be read: ${safeThrownValue(error)}`,
    );
  }

  try {
    const graph = new Graph(materialized);
    const definitions = graph.typed(classes.definition);
    const applications = graph.typed(classes.application);
    if (definitions.length === 0) {
      issues.push(
        issue(
          "ontology-root-missing",
          p("UXPatternDefinition"),
          "Ontology admission requires at least one UXPatternDefinition root.",
        ),
      );
    }
    if (applications.length === 0) {
      issues.push(
        issue(
          "ontology-root-missing",
          p("ApplicationBlueprint"),
          "Ontology admission requires at least one ApplicationBlueprint root.",
        ),
      );
    }
    if (applications.length !== 1) {
      issues.push(
        issue(
          "application-envelope-invalid",
          p("ApplicationBlueprint"),
          `Ontology admission requires exactly one ApplicationBlueprint root; received ${applications.length}.`,
        ),
      );
    }
    const applicationIdentities = new Map<string, string[]>();
    const applicationInstanceIdentities = new Map<string, string[]>();
    const ownedResources = new Map<string, string[]>();
    for (const application of applications) {
      const libraryId = graph.one(application, "applicationBlueprintId");
      const version = graph.one(application, "version");
      const instanceId = graph.one(application, "applicationInstanceId");
      if (libraryId && version) {
        const identity = `${libraryId}@${version}`;
        const owners = applicationIdentities.get(identity) ?? [];
        owners.push(application);
        applicationIdentities.set(identity, owners);
      }
      if (instanceId) {
        const owners = applicationInstanceIdentities.get(instanceId) ?? [];
        owners.push(application);
        applicationInstanceIdentities.set(instanceId, owners);
      }
      for (const predicate of ["hasUxInstance", "hasBinding", "hasJourney"]) {
        for (const resource of graph.refs(application, predicate)) {
          const owners = ownedResources.get(resource) ?? [];
          owners.push(application);
          ownedResources.set(resource, owners);
        }
      }
    }
    for (const [identity, owners] of [
      ...applicationIdentities,
      ...applicationInstanceIdentities,
      ...ownedResources,
    ]) {
      if (new Set(owners).size < 2) continue;
      issues.push(
        issue(
          "application-envelope-invalid",
          p("ApplicationBlueprint"),
          `Application identity or owned resource "${identity}" is shared by multiple roots: ${[...new Set(owners)].sort().join(", ")}.`,
        ),
      );
    }

    const definitionsByRef = new Map<string, string[]>();
    for (const definition of definitions) {
      const id = graph.one(definition, "uxPatternId");
      const version = graph.one(definition, "version");
      if (!id || !version) continue;
      const definitionRef = `${id}@${version}`;
      const resources = definitionsByRef.get(definitionRef) ?? [];
      resources.push(definition);
      definitionsByRef.set(definitionRef, resources);
    }
    for (const [definitionRef, resources] of definitionsByRef) {
      if (resources.length < 2) continue;
      issues.push(
        issue(
          "duplicate-ux-pattern-ref",
          p("definitionRef"),
          `Duplicate UX definition reference "${definitionRef}": ${resources.sort().join(", ")}.`,
        ),
      );
    }

    for (const definition of definitions) {
      validateDefinition(graph, definition, authority.value, issues);
    }
    for (const application of applications) {
      validateApplication(graph, application, authority.value, issues);
    }
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `UX application library RDF semantic validation failed: ${safeThrownValue(error)}`,
    );
  }

  try {
    const sorted = dedupeAndSort(issues);
    return { ok: sorted.length === 0, issues: sorted };
  } catch (error) {
    return rdfInputFailure(
      "quads",
      `UX application library issues could not be finalized: ${safeThrownValue(error)}`,
    );
  }
}
